import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload,
  Trash2,
  Loader2,
} from 'lucide-react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { splitPDF, formatFileSize } from '@/lib/pdfOperations';
import PageCanvas from '@/components/PageCanvas';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

let pageIdCounter = 0;

/**
 * DeletePages — Standalone tool for removing pages from a PDF.
 *
 * Select pages to delete, preview with red overlay, then save
 * a new PDF with only the kept pages.
 */
export default function DeletePages() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const { openFile } = useFileDialog();

  // Document state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [pages, setPages] = useState([]);

  // Selection = pages marked for deletion
  const [selected, setSelected] = useState(new Set());

  // UI state
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ==================== File handling ====================

  const loadFile = useCallback(
    async (file) => {
      if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;
      if (pdfDoc) pdfDoc.destroy();

      setError(null);
      setFileName(file.name);
      setFileSize(file.size);

      try {
        // Decrypt owner-password PDFs (lossless) / reject truly locked ones
        // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
        const buf = await readPdfLibBytes(file);
        setArrayBuffer(buf);
        const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
        setPdfDoc(doc);
        rememberDocument(file);

        const newPages = Array.from({ length: doc.numPages }, (_, i) => ({
          id: `p-${++pageIdCounter}`,
          pageNum: i + 1,
        }));
        setPages(newPages);
        setSelected(new Set());
      } catch (err) {
        setError(pdfIntakeErrorMessage(err, 'delete pages'));
      }
    },
    [pdfDoc, rememberDocument]
  );

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (!result) return;
    const file = new File([result.buffer], result.name, { type: 'application/pdf' });
    loadFile(file);
  }, [openFile, loadFile]);

  // ==================== Selection (= marking for deletion) ====================

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === pages.length ? new Set() : new Set(pages.map((p) => p.id))
    );
  }, [pages]);

  const allSelected = pages.length > 0 && selected.size === pages.length;
  const someSelected = selected.size > 0 && selected.size < pages.length;
  const allMarked = allSelected; // can't delete all pages

  // ==================== Delete action ====================

  const handleDelete = useCallback(async () => {
    if (!arrayBuffer || selected.size === 0 || allMarked) return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }

    // Keep pages that are NOT selected for deletion
    const keepPageNums = pages.filter((p) => !selected.has(p.id)).map((p) => p.pageNum);

    setProcessing(true);
    setProgress(0);
    setError(null);

    try {
      const bytes = await splitPDF(arrayBuffer, keepPageNums, setProgress);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const baseName = fileName.replace(/\.pdf$/i, '');
      const file = new File([blob], `${baseName}_trimmed.pdf`, { type: 'application/pdf' });
      if (usageCapApplies()) incrementUsage();
      navigate('/viewer', { state: { file } });
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
      setProcessing(false);
    }
  }, [arrayBuffer, selected, allMarked, pages, fileName, navigate]);

  // ==================== Render ====================

  const isSinglePage = pages.length === 1;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">Delete Pages</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto w-full p-6 flex-1 flex flex-col">
        {/* Upload / File info */}
        {!pdfDoc ? (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                isDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.01]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
              <p className="text-sm text-gray-400 mt-1">Select pages to remove from your document</p>
            </div>
            <CloudSourceHint
              onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
              className="mb-6"
            />
            {error && <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>}
          </>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3">
            <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
              <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
              <p className="text-xs text-gray-500">{pages.length} page{pages.length !== 1 ? 's' : ''} · {formatFileSize(fileSize)}</p>
            </div>
            <button onClick={handleBrowse} className="text-sm text-brand-blue hover:underline font-medium">Change file</button>
          </div>
        )}

        {/* Toolbar + Page Grid */}
        {pdfDoc && (
          <>
            {/* Toolbar row */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                />
                <span className="text-sm text-gray-700">Select all</span>
              </label>
              <span className="text-sm text-gray-500">{selected.size} of {pages.length} selected</span>
              <span className="ml-auto text-sm text-gray-400">Select pages to delete</span>
            </div>

            {isSinglePage && (
              <div className="text-center py-4 text-sm text-gray-500 bg-gray-100 rounded-lg mb-4">
                This PDF has only one page — deletion is not available.
              </div>
            )}

            {/* Page grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 mb-24">
              {pages.map((page) => (
                <DeletePageCard
                  key={page.id}
                  page={page}
                  pdfDoc={pdfDoc}
                  isSelected={selected.has(page.id)}
                  onToggleSelect={() => toggleSelect(page.id)}
                  disabled={isSinglePage}
                />
              ))}
            </div>

            {/* Sticky action bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-10">
              <div className="max-w-5xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {error && <p className="text-red-600 text-sm">{error}</p>}
                  {processing && (
                    <div className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin text-brand-blue" />
                      <span className="text-sm text-gray-600">{Math.round(progress * 100)}%</span>
                    </div>
                  )}
                  {!processing && selected.size > 0 && (
                    <p className={`text-sm ${allMarked ? 'text-red-600 font-medium' : 'text-red-600'}`}>
                      {allMarked
                        ? "You can't delete all pages"
                        : `${selected.size} page${selected.size !== 1 ? 's' : ''} will be removed`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/')}
                    className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={processing || selected.size === 0 || allMarked || isSinglePage}
                    className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={16} />
                    Delete {selected.size > 0 ? `${selected.size} Page${selected.size !== 1 ? 's' : ''}` : 'Pages'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
  );
}

// ==================== Sub-component ====================

function DeletePageCard({ page, pdfDoc, isSelected, onToggleSelect, disabled }) {
  return (
    <div
      onClick={disabled ? undefined : onToggleSelect}
      className={`relative rounded-xl overflow-hidden border-2 transition-all duration-150 group ${
        disabled
          ? 'border-gray-200 opacity-50 cursor-not-allowed'
          : isSelected
            ? 'border-red-400 shadow-sm cursor-pointer'
            : 'border-gray-200 opacity-60 hover:opacity-90 hover:border-gray-300 cursor-pointer'
      }`}
    >
      {/* Checkbox */}
      {!disabled && (
        <div className="absolute top-1.5 left-1.5 z-10" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={isSelected} onChange={onToggleSelect}
            className="w-4.5 h-4.5 rounded border-gray-300 text-red-500 focus:ring-red-500 cursor-pointer" />
        </div>
      )}

      {/* Delete hover button */}
      {!disabled && !isSelected && (
        <div
          className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onToggleSelect}
            className="p-1.5 rounded-lg bg-white/90 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors shadow-sm"
            title="Mark for deletion"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}

      <PageCanvas pdfDoc={pdfDoc} pageNumber={page.pageNum} scale={0.18} />

      {/* Red overlay for selected (marked for deletion) */}
      {isSelected && (
        <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
          <Trash2 size={24} className="text-red-500" />
        </div>
      )}

      {/* Page number badge */}
      <div className={`absolute bottom-0 inset-x-0 text-center py-0.5 text-xs font-medium ${
        isSelected ? 'bg-red-500 text-white' : 'bg-gray-800/60 text-white'
      }`}>
        {page.pageNum}
      </div>
    </div>
  );
}
